import useMerchantTexts from '@/composables/merchantTexts'

import {
  MAX_CONTEST_REWARDS,
  calculateRemainingProbability,
  calculateTotalProbability,
  countRewardPlaceholders,
  createNewReward,
  createRewardFromLibrary,
  distributeProbabilityEvenly,
  getPointsDefaultNotificationText,
  getRewardDefaultNotificationText,
  hasProbabilityError as hasProbabilityErrorUtil,
  mergeLibraryRewards,
  roundToDecimals,
  updateRewardProbability
} from '../utils'

import type { Contest, ContestGameReward, LoyaltyReward } from '@/types/types'

export function useContestRewards(initialContest: Contest, emit: (event: string, ...args: any[]) => void) {
  const { t } = useI18n()
  const { isPolite } = useMerchantTexts()

  const convertContestProbabilities = (contestData: Contest): Contest => {
    const convertedContest = { ...contestData }
    convertedContest.contestGameRewards = contestData.contestGameRewards.map((reward) => ({
      ...reward,
      probability: roundToDecimals(reward.probability),
      pictureUrl: reward?.reward?.pictureUrl || reward.pictureUrl
    }))
    return convertedContest
  }

  const contest = ref<Contest>(convertContestProbabilities(initialContest))

  const openedRewardModalId = ref<number | null>(null)
  const openedModalType = ref<string | null>(null)
  const selectedReward = ref<ContestGameReward | null>(null)

  const computedWheelRewards = computed(() => {
    const wheelRewards = contest.value.contestGameRewards.map((reward, index) => ({
      id: index + 1,
      bgColor: reward.color,
      color: reward.textColor,
      value: reward.name,
      probability: reward.probability
    }))

    return wheelRewards
  })

  const totalProbability = computed(() => calculateTotalProbability(contest.value.contestGameRewards))
  const remainingProbability = computed(() => calculateRemainingProbability(contest.value.contestGameRewards))
  const hasProbabilityError = computed(() => hasProbabilityErrorUtil(contest.value.contestGameRewards))

  const tableRows = computed(() =>
    contest.value.contestGameRewards.map((reward, index) => ({
      id: index,
      data: reward
    }))
  )

  const emitChange = () => {
    const contestForApi = { ...contest.value }
    contestForApi.contestGameRewards = contest.value.contestGameRewards.map((reward) => {
      return {
        ...reward,
        probability: roundToDecimals(reward.probability)
      }
    })

    emit('update:modelValue', contestForApi)
  }

  const updateReward = (newReward: ContestGameReward, index: number) => {
    if (index === null) {
      contest.value.contestGameRewards.push(newReward)
    } else {
      contest.value.contestGameRewards[index] = newReward
    }
    emitChange()
  }

  const updateRewardPercentage = (index: number, value: number) => {
    updateRewardProbability(contest.value.contestGameRewards[index], value)
    emitChange()
  }

  const deleteReward = (index: number) => {
    contest.value.contestGameRewards.splice(index, 1)
    emitChange()
  }

  const editReward = (index: number) => {
    openedRewardModalId.value = index
    let slug: string = contest.value.contestGameRewards[index].rewardTypeSlug
    if (
      slug === 'free_product' ||
      slug === 'voucher_discount_percent' ||
      slug === 'voucher_discount_amount' ||
      slug === 'voucher_free_delivery'
    ) {
      slug = 'gift'
    }
    openedModalType.value = slug
    selectedReward.value = { ...contest.value.contestGameRewards[index] }
  }

  const addNewReward = (type: string) => {
    if (type === 'lost') {
      const existingLostReward = contest.value.contestGameRewards.find((reward) => reward.rewardTypeSlug === 'lost')
      if (existingLostReward) {
        return { success: false, message: 'lost_already_exists' }
      }
    }

    const colors = {
      color: contest.value.backgroundColor,
      textColor: contest.value.textColor
    }

    if (type === 'gift') {
      selectedReward.value = createNewReward({
        type: 'gift',
        index: contest.value.contestGameRewards.length,
        colors,
        t
      })
      selectedReward.value.notificationTextOverride = getRewardDefaultNotificationText(t, isPolite.value)

      openedModalType.value = 'gift'

      return { success: true, message: 'gift_added' }
    } else {
      const newReward = createNewReward({ type, index: contest.value.contestGameRewards.length, colors, t })

      if (type === 'points') {
        newReward.notificationTextOverride = getPointsDefaultNotificationText(t, isPolite.value)
      }

      contest.value.contestGameRewards.push(newReward)
      emitChange()

      return { success: true, message: type === 'lost' ? 'lost_added' : 'reward_added' }
    }
  }

  const rewardPlaceholdersCount = computed(() => countRewardPlaceholders(contest.value.contestGameRewards))

  const librarySelectionLimit = computed(() =>
    rewardPlaceholdersCount.value > 0
      ? rewardPlaceholdersCount.value
      : Math.max(0, MAX_CONTEST_REWARDS - contest.value.contestGameRewards.length)
  )

  const addLibraryRewards = (libraryRewards: LoyaltyReward[]) => {
    const currentRewards = contest.value.contestGameRewards
    const hasPlaceholders = rewardPlaceholdersCount.value > 0
    const colors = { color: contest.value.backgroundColor, textColor: contest.value.textColor }

    const newRewards = libraryRewards
      .slice(0, librarySelectionLimit.value)
      .map((reward, index) =>
        createRewardFromLibrary({ reward, index: currentRewards.length + index, colors, t, isPolite: isPolite.value })
      )
    if (!newRewards.length) return

    const mergedRewards = mergeLibraryRewards(currentRewards, newRewards)
    contest.value.contestGameRewards = hasPlaceholders ? mergedRewards : distributeProbabilityEvenly(mergedRewards)
    emitChange()
  }

  const closeRewardModal = () => {
    openedRewardModalId.value = null
    openedModalType.value = null
    selectedReward.value = null
  }

  const handleRewardUpdate = (newReward: ContestGameReward) => {
    updateReward(newReward, openedRewardModalId.value)
    closeRewardModal()
  }

  return {
    contest,
    openedRewardModalId,
    openedModalType,
    selectedReward,

    computedWheelRewards,
    totalProbability,
    remainingProbability,
    hasProbabilityError,
    tableRows,

    updateReward,
    updateRewardPercentage,
    deleteReward,
    editReward,
    addNewReward,
    addLibraryRewards,
    librarySelectionLimit,
    closeRewardModal,
    handleRewardUpdate
  }
}
